import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useBrandProvider from '@hooks/useBrandProvider'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ModalSubHeader from '@components/ModalComponents/ModalSubHeader'

import SelectBrands from './components/SelectBrands'
import SelectProviders from './components/SelectProviders'

import useBulkCreateBrandProviderModal from './hooks/useBulkCreateBrandProviderModal'

const MODAL_KEY = 'BulkCreateBrandProviderModal'

type ModalCallbacks = {
  closeModal: () => void,
  bulkCreateBrandProviders: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const BulkCreateBrandProviderModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, bulkCreateBrandProviders } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { brandProvider: initBrandProvider } = modalPayload

  const bulkCreateBrandProviderPayload = useBulkCreateBrandProviderModal(initBrandProvider)
  const {
    brand,
    creating,
    entityParams,
    provider,
    saveEnabled,
  } = bulkCreateBrandProviderPayload

  const {
    callbacks: { bulkCreateBrandProviders: createFn },
  } = useBrandProvider(initBrandProvider)

  const bulkCreateBrandProvidersPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams,
    requestOptions: {
      include_brand_provider_brand: !!initBrandProvider.provider_id,
      include_brand_provider_provider: !!initBrandProvider.brand_id,
    },
    toastText: 'Brand Provider Created',
  }

  const entityText = initBrandProvider.brand_id ? 'Providers' : 'Brands'
  const ownerTitle = initBrandProvider.brand_id ? brand?.title : provider?.title
  const subHeaderText = initBrandProvider.brand_id ? 'Provider' : 'Brand'

  return (
    <SidebarModal callbacks={callbacks} modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Add" titleSecondLine={entityText} />

      <ModalSubHeader title={`Assign a ${subHeaderText} to`} ownerTitle={ownerTitle} />

      <SidebarModal.Content hasSubHeader>
        <Form>
          {!!initBrandProvider.provider_id && (
            <SelectBrands bulkCreateBrandProviderPayload={bulkCreateBrandProviderPayload} />
          )}

          {!!initBrandProvider.brand_id && (
            <SelectProviders bulkCreateBrandProviderPayload={bulkCreateBrandProviderPayload} />
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => bulkCreateBrandProviders(bulkCreateBrandProvidersPayload)}
          size="large"
        >
          {saveEnabled ? `Add ${entityText}` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkCreateBrandProviderModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
